<template>
  <div>
    <div class="main-content">
      <div class="section__content section__content--p30">
        <div class="container-fluid create-job-container p-b-40">
          <v-form>
            <v-card class="p-b-30 p-t-30 p-l-20">
              <div class="el-loading-mask" v-if="loading">
                <div class="el-loading-spinner">
                  <svg viewBox="25 25 50 50" class="circular">
                    <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
                  </svg>
                </div>
              </div>
              <!-- Title -->
              <div class="row">
                <div class="btn-actions-pane-left">
                  <h3 class="title-1 p-l-140">
                    <v-btn
                      text
                      icon
                      class="m-0"
                      @click="$router.back()">
                      <i class="zmdi zmdi-long-arrow-left"></i>
                    </v-btn>
                    <span v-if="!isEditing">
                      {{ text('New Endpoint') }}
                    </span>
                    <span v-else>
                       {{ text('edit_endpoint') }} <span class="p-l-20 status--gray"> ID={{ form.id }}</span>
                    </span>
                  </h3>
                </div>
              </div>
              <!-- Name and Origin URL -->
              <div class="section">
                <div class="row p-b-10">
                  <div class="col-md-2 col-l"></div>
                  <div class="col-md-10 section-description">
                    {{ text('new_endpoint_text') }}
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-2 col-l"></div>
                  <div class="col-md-10 p-r-0">
                    <v-text-field
                      v-model.trim="form.name"
                      id="name"
                      :label="text('title_label')"
                      :counter="255"
                      :rules="rules.nameRules"
                      required>
                    </v-text-field>
                  </div>
                </div>
                <div class="row p-t-20">
                  <div class="col-md-2 col-l"></div>
                  <div class="col-md-10 p-r-0">
                    <v-text-field
                      v-model.trim="form.originURL"
                      :label="text('ip_domain')"
                      :hint="text('http_hint')"
                      @change="onOriginURLChange"
                      counter="1024">
                    </v-text-field>
                  </div>
                </div>
              </div>
              <!-- INPUT -->
              <div class="section whitelist-section">
                <div class="row p-b-15">
                  <div class="col-md-2 col-l">
                    <h2>{{ text('whitelist') }}</h2>
                  </div>
                  <div class="col-md-10 section-description">
                    {{ text('whitelist_text') }}
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-2 col-l"></div>
                  <div class="col-md-10 p-r-0">
                    <div class="row">
                      <div class="col-md-3 p-r-0" style="max-width: 210px;">
                        <v-radio-group v-model="form.newSubnet.type" row @change="typeChanged">
                          <v-radio
                            :label="text('host')"
                            value="host"></v-radio>
                          <v-radio
                            :label="text('subnet')"
                            value="subnet"></v-radio>
                        </v-radio-group>
                      </div>
                      <div class="col-md-5 p-l-0" style="max-width: 180px;">
                        <v-text-field
                          clearable
                          v-model.trim="form.newSubnet.ip"
                          label="IP"
                          :rules="rules.ipRule"
                          placeholder="192.168.1.1">
                        </v-text-field>
                      </div>
                      <div class="col-md-5 p-l-0" style="max-width: 75px;">
                        <v-text-field
                          :label="text('mask')"
                          :disabled="form.newSubnet.type === 'host'"
                          v-model.trim="form.newSubnet.mask"
                          :rules="rules.maskRule">
                        </v-text-field>
                      </div>
                      <div class="col-md-2 p-t-10">
                        <v-btn
                          depressed
                          :disabled="!isSubnetFormValid"
                          @click.stop="addSubnet">
                          {{ text('add_btn') }}
                        </v-btn>
                      </div>
                    </div>
                    <div class="row p-t-10" v-if="form.ipWhitelist.length">
                      <div class="col-md-12">
                        <h5>{{ text('whitelist') }}:</h5>
                        <table class="m-t-10">
                          <tr v-for="(subnet, i) of form.ipWhitelist" :key="i">
                            <td :class="{'bg-flat-color-3': subnet.ip === (form.newSubnet.ip + '/' + form.newSubnet.mask)}">
                              {{ subnet.ip }}
                            </td>
                            <td class="p-l-10">
                              <v-btn
                              text icon
                              class="m-0"
                              @click="removeSubnet(i)">
                              <i class="zmdi zmdi-delete"></i>
                            </v-btn>
                            </td>
                          </tr>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Action button -->
              <div class="row text-right p-t-10">
                <div class="col-md-2 col-l"></div>
                <div class="col-md-10">
                  <v-btn
                    :disabled="!isNewFormValid"
                    color="success"
                    @click="onSubmit">
                    <span v-if="!isEditing">{{ text('create_btn') }}</span>
                    <span v-else>{{ text('save_btn') }}</span>
                  </v-btn>
                </div>
              </div>
            </v-card>
          </v-form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { parseURL, joinURL } from '@/helpers/utils';
import { endpointToJsonAPI, JsonAPIEndpointToNorm } from '@/components/cdn/shared/converter';

// const ipAddressWithMaskRegExp = /^([1-9]|[1-9]\d|1\d{2}|2[0-4]\d|25[0-5])(\.(\d|[1-9]\d|1\d{2}|2[0-4]\d|25[0-5])){3}\/\d{1,2}$/;
const ipAddressRegExp = /^([1-9]|[1-9]\d|1\d{2}|2[0-4]\d|25[0-5])(\.(\d|[1-9]\d|1\d{2}|2[0-4]\d|25[0-5])){3}$/;

export default {
  data() {
    return {
      isEditing: false,
      form: {
        id: 0,
        name: '',
        originURL: 'http://',
        newSubnet: {
          type: 'host',
          ip: '',
          mask: '32',
        },
        ipWhitelist: [],
      },
      rules: {
        nameRules: [
          v => !!v || this.text('non_empty_err'),
          v => (v.length >= 3 && v.length <= 255) || this.text('title_min_err'),
        ],
        ipRule: [
          v => (!v || ipAddressRegExp.test(v.trim().toString())) || this.text('invalid_ip'),
        ],
        maskRule: [
          v => !!v || this.text('required'),
          v => (v >= 16 && v <= 32) || '16-32',
        ],
      },
    };
  },
  created() {
    const { id } = this.$route.params;
    if (id) {
      this.isEditing = true;
      this.$store.commit('setPageLoader');
      this.$store.dispatch('endpoints/getCDNEndpoint', id)
        .then((endpoint) => {
          const processed = JsonAPIEndpointToNorm(endpoint);
          this.form.id = processed.id;
          this.form.name = processed.name;
          this.form.originURL = processed.originURL;
          if (processed.ipWhitelist.length) {
            this.form.ipWhitelist = processed.ipWhitelist;
          }
        })
        .catch(() => {
          this.$router.push({ name: 'EndpointListPage' });
        })
        .finally(() => this.$store.commit('resolvePageLoader'));
    }
  },
  computed: {
    loading() {
      return this.$store.getters.isDataLoaded;
    },
    isNewFormValid() {
      if (this.form.name < 3) return false;
      return this.form.originURL !== 'http://';
    },
    isSubnetFormValid() {
      const ip = this.form.newSubnet.ip || '';
      if (!ipAddressRegExp.test(ip.toString())) {
        return false;
      }
      return this.form.newSubnet.mask >= 16 && this.form.newSubnet.mask <= 32;
    },
    dict() {
      return this.$store.getters.dictionary;
    },
  },
  methods: {
    onOriginURLChange() {
      const parsed = parseURL(this.form.originURL);
      parsed.protocol = 'http';
      this.form.originURL = joinURL(parsed);
    },
    typeChanged() {
      if (this.form.newSubnet.type === 'host') {
        this.form.newSubnet.mask = 32;
      }
    },
    addSubnet() {
      const newSubnet = `${this.form.newSubnet.ip}/${this.form.newSubnet.mask}`;
      for (let i = 0; i < this.form.ipWhitelist.length; i += 1) {
        if (this.form.ipWhitelist[i].ip === newSubnet) {
          return;
        }
      }
      this.form.ipWhitelist.push({ ip: newSubnet });
    },
    removeSubnet(i) {
      this.form.ipWhitelist.splice(i, 1);
    },
    onSubmit() {
      if (!this.isNewFormValid) return;

      const formData = endpointToJsonAPI(this.form);

      if (this.isEditing) {
        this.$store.dispatch('endpoints/updateCDNEndpoint', formData)
          .then(() => {
            this.$toasted.success(this.text('save_changes_popup'), {
              theme: 'toasted-primary',
              position: 'top-center',
              duration: 5000,
            });
            this.$router.push({ name: 'EndpointDetailPage' });
          })
          .catch((errorMsg) => {
            this.$toasted.error(errorMsg, {
              theme: 'toasted-primary',
              position: 'top-center',
              duration: 7000,
            });
          });
        return;
      }

      this.$store.dispatch('endpoints/createCDNEndpoint', formData)
        .then(() => {
          this.$toasted.success(this.text('new_endpoint_popup'), {
            theme: 'toasted-primary',
            position: 'top-center',
            duration: 7000,
          });
          this.$router.push({ name: 'EndpointListPage' });
        })
        .catch((errorMsg) => {
          this.$toasted.error(errorMsg, {
            theme: 'toasted-primary',
            position: 'top-center',
            duration: 7000,
          });
        });
    },
    text(code) {
      // eslint-disable-next-line no-prototype-builtins
      if (this.dict.hasOwnProperty(code)) {
        return this.dict[code];
      }
      return code;
    },
  },
  watch: {
    $route(to, from) {
      if (to.name === 'EndpointNewPage') {
        window.location.reload();
      }
    },
  },
};
</script>
